import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { getSDK, getDeviceIdAsync, useDeviceId } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import type {
  AddLocalMCPRequest,
  LocalMCPServer,
  UpdateLocalMCPRequest,
  UpdateLocalMCPResponse,
  ShogunChopMcpServerDefinition,
  ControlMCPResponse,
  ListMCPToolsResponse,
  McpControlAction,
  RegisterMCPRequest,
  RegisterMCPResponse,
  UpdateMCPRequest,
  UpdateMCPResponse,
  DeleteMCPResponse,
} from '@localagent/sdk';
import { mergeToolsServers, type ToolsServers } from './tools-servers';

export function useMCPServers() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const deviceId = useDeviceId();

  return useQuery<ToolsServers, Error>({
    queryKey: ['mcp-servers', deviceId],
    queryFn: async () => {
      const sdk = getSDK();
      const [device, account] = await Promise.all([
        sdk.tools.listMCPs(deviceId!),
        sdk.tools.listAccountMCPs(),
      ]);
      return mergeToolsServers(device, account, deviceId!);
    },
    enabled: Boolean(isAuthenticated && sdkConfigured && deviceId),
  });
}

export function useMCPTools(mcpId: string | null) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<ListMCPToolsResponse, Error>({
    queryKey: ['mcp-tools', mcpId],
    queryFn: () => getSDK().tools.listTenantMCPTools(mcpId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && mcpId),
  });
}

function useInvalidateTools() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();
  return () => {
    if (isAuthenticated) {
      void queryClient.invalidateQueries({ queryKey: ['mcp-servers'] });
      void queryClient.invalidateQueries({ queryKey: ['tool-catalog'] });
    }
  };
}

export function useRegisterMCP() {
  const invalidate = useInvalidateTools();
  return useMutation<RegisterMCPResponse, Error, RegisterMCPRequest>({
    mutationFn: async (request) => getSDK().tools.registerMCP(await getDeviceIdAsync(), request),
    onSuccess: invalidate,
  });
}

export function useUpdateMCP() {
  const invalidate = useInvalidateTools();
  return useMutation<UpdateMCPResponse, Error, { deviceId: string; mcpId: string; request: UpdateMCPRequest }>({
    mutationFn: ({ deviceId, mcpId, request }) => getSDK().tools.updateMCP(deviceId, mcpId, request),
    onSuccess: invalidate,
  });
}

export function useDeleteMCP() {
  const invalidate = useInvalidateTools();
  return useMutation<DeleteMCPResponse, Error, { deviceId: string; mcpId: string }>({
    mutationFn: ({ deviceId, mcpId }) => getSDK().tools.deleteMCP(deviceId, mcpId),
    onSuccess: invalidate,
  });
}

export function useControlMCP() {
  const invalidate = useInvalidateTools();
  return useMutation<ControlMCPResponse, Error, { deviceId: string; mcpId: string; action: McpControlAction }>({
    mutationFn: ({ deviceId, mcpId, action }) => getSDK().tools.controlMCP(deviceId, mcpId, action),
    onSettled: invalidate,
  });
}

export function useAddLocalMCP() {
  const invalidate = useInvalidateTools();
  return useMutation<LocalMCPServer, Error, AddLocalMCPRequest>({
    mutationFn: (request) => getSDK().tools.addLocalMCP(request),
    onSuccess: invalidate,
  });
}

export function useUpdateLocalMCP() {
  const invalidate = useInvalidateTools();
  const queryClient = useQueryClient();
  return useMutation<UpdateLocalMCPResponse, Error, { mcpId: string; request: UpdateLocalMCPRequest }>({
    mutationFn: ({ mcpId, request }) => getSDK().tools.updateLocalMCP(mcpId, request),
    onSettled: (_data, _err, { mcpId }) => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['local-mcp-definition', mcpId] });
    },
  });
}

export function useLocalMcpDefinition(mcpId: string | null) {
  return useQuery<ShogunChopMcpServerDefinition, Error>({
    queryKey: ['local-mcp-definition', mcpId],
    queryFn: () => getSDK().shogunchop.mcpServerDefinition(mcpId!),
    enabled: Boolean(mcpId),
    gcTime: 0,
    staleTime: 0,
  });
}
